import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

/**
 * Query factories: the only way UI code reaches the delivery archive.
 *
 * `source()` used to resolve to a sample archive, because there was no
 * delivery table yet (A26). It landed on 2026-10-31: the archive is
 * `public_deliveries`/`public_delivery_media`, managed by Kabex from Kabex
 * Hub, and the sample material (and the "Sample material" notice drawn from
 * its presence) is deleted.
 *
 * There is deliberately NO `env.useMockData` branch, matching
 * `services/accessories/queries.ts` and not `services/vehicles/queries.ts`:
 * the vehicle mock exists because the stock table can legitimately be empty
 * in a development project, but this mock existed because there was no table
 * at all. An empty archive now renders the gallery's own empty state, which
 * is the honest answer to an empty archive.
 */
import * as source from './deliveries.api';
import { type DeliveryListFilters } from './types';

/** How many tiles a page of the gallery holds. Mirrors the source's own default. */
export const DELIVERY_PAGE_SIZE = 12;

export const deliveryKeys = {
  all: ['deliveries'] as const,
  gallery: (filters: DeliveryListFilters) => [...deliveryKeys.all, 'gallery', filters] as const,
  showcase: (limit: number) => [...deliveryKeys.all, 'showcase', limit] as const,
  facets: () => [...deliveryKeys.all, 'facets'] as const,
};

/**
 * The gallery grid, paged.
 *
 * Infinite rather than numbered: the archive is browsed, not searched, and a
 * visitor who has swiped through forty tiles has no use for a page 3. Pages
 * are kept, rather than the whole list refetched at a larger size, so pressing
 * "Load more" never re-downloads what is already on screen.
 *
 * `page` is left out of the key on purpose — it is the cursor, and TanStack
 * stores the pages under the one key that identifies the filtered list.
 */
export const deliveryGalleryQuery = (filters: Omit<DeliveryListFilters, 'page'> = {}) =>
  infiniteQueryOptions({
    queryKey: deliveryKeys.gallery(filters),
    queryFn: ({ pageParam }) =>
      source.listDeliveryMedia({
        ...filters,
        page: pageParam,
        pageSize: filters.pageSize ?? DELIVERY_PAGE_SIZE,
      }),
    initialPageParam: 1,
    /** Undefined stops the pager, which is what `hasNextPage` reads. */
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  });

/** The proof strip on the pages that link into the gallery. */
export const deliveryShowcaseQuery = (limit = 6) =>
  queryOptions({
    queryKey: deliveryKeys.showcase(limit),
    queryFn: () => source.listDeliveryShowcase(limit),
    staleTime: 5 * 60_000,
  });

/** The chip row's options and counts. */
export const deliveryFacetsQuery = () =>
  queryOptions({
    queryKey: deliveryKeys.facets(),
    queryFn: () => source.getDeliveryFacets(),
    staleTime: 5 * 60_000,
  });
